/**
 * Floating add-to-cart bar for the main product section.
 *
 * Shows once the main add-to-cart button has scrolled above the viewport and
 * hides when it is back in view. Clicking the bar either clicks the main
 * submit button (so all form properties and app interceptors still apply) or,
 * when a choice is still outstanding, moves the shopper to that field.
 *
 * Sold out: the Back in Stock app (backinstock.useamp.com) injects #BIS_trigger
 * into the form after load and the main button is hidden. The bar then mirrors
 * that button instead ("Notify me when available") and clicks it to open the
 * app's sign-up modal.
 * Markup: snippets/cf-sticky-add-to-cart.liquid
 */
if (!customElements.get('cf-sticky-atc')) {
  customElements.define(
    'cf-sticky-atc',
    class CfStickyAtc extends HTMLElement {
      connectedCallback() {
        const sectionId = this.dataset.section;
        this.form = document.getElementById(`product-form-${sectionId}`);
        this.mainButton = this.form && this.form.querySelector('[name="add"]');
        // No main button means nothing to mirror; inside quick-add the page's own bar applies.
        if (!this.mainButton || !('IntersectionObserver' in window) || this.closest('quick-add-modal')) return;

        this.bar = this.querySelector('.cf-sticky-atc');
        this.button = this.querySelector('.cf-sticky-atc__button');
        this.buttonLabel = this.querySelector('.cf-sticky-atc__button-label');
        this.icon = this.querySelector('.cf-sticky-atc__icon');
        this.notifyIcon = this.querySelector('.cf-sticky-atc__notify-icon');
        this.spinner = this.querySelector('.loading-overlay__spinner');
        this.priceContainer = this.querySelector('.cf-sticky-atc__price .price__container');
        this.image = this.querySelector('.cf-sticky-atc__image');
        this.error = this.querySelector('.cf-sticky-atc__error');

        this.mainPrice = document.getElementById(`price-${sectionId}`);
        this.errorWrapper = this.form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
        this.variantPicker = document.querySelector(
          `variant-radios[data-section="${sectionId}"], variant-selects[data-section="${sectionId}"]`
        );
        this.braceletSelect = this.form.querySelector('select[name="properties[Bracelet Size]"]');
        // The bar's own option selects (products with variants). The page's visible picker may be
        // the Swatch King app, which drives Dawn's hidden selects; drive it so both stay in sync.
        this.optionSelects = Array.from(this.querySelectorAll('.cf-sticky-atc__select'));
        this.productSection = this.closest('.shopify-section') || document;
        this.variantChosen = this.dataset.variantChosen === 'true';
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        // Site newsletter popup (snippets/newsletter-popup.liquid): z-index 9999, below the bar,
        // and it doesn't lock page scroll, so it needs its own check.
        this.newsletterPopup = document.getElementById('cf-newsletter-popup');
        this.notifyButton = null;
        this.notifyFrame = null;
        this.hasCartDrawer = Boolean(document.querySelector('cart-drawer'));
        this.visible = false;
        this.scrolledPast = false;
        this.overlayOpen = this.isOverlayOpen();

        this.onClick = this.onClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onOptionChange = this.onOptionChange.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
        this.syncState = this.syncState.bind(this);

        this.button.addEventListener('click', this.onClick);
        this.variantPicker?.addEventListener('change', this.onVariantChange);
        this.optionSelects.forEach((select) => select.addEventListener('change', this.onOptionChange));
        this.braceletSelect?.addEventListener('change', this.syncState);
        document.addEventListener('focusin', this.onFocusIn);

        // The sticky header covers the top of the viewport once the page is scrolled, so a
        // main button sitting under it is in the viewport but not visible. Track the area it
        // covers and re-observe when it changes (sticky on/off, reveal/hide, resize).
        this.headerSection = document.querySelector('sticky-header') && document.getElementById('shopify-section-header');
        this.observeMainButton();
        if (this.headerSection) {
          this.scheduleHeaderCheck = this.scheduleHeaderCheck.bind(this);
          this.headerObserver = new MutationObserver(this.scheduleHeaderCheck);
          this.headerObserver.observe(this.headerSection, { attributes: true, attributeFilter: ['class'] });
          this.headerResizeObserver = new ResizeObserver(this.scheduleHeaderCheck);
          this.headerResizeObserver.observe(this.headerSection);
          this.headerSection.addEventListener('transitionend', this.scheduleHeaderCheck);
        }

        // Hide while a drawer/modal has locked page scroll (cart drawer, menu drawer,
        // search, media modal) or the newsletter popup is open, so the bar never sits
        // over it or joins its tab order.
        this.overlayObserver = new MutationObserver(() => {
          const overlayOpen = this.isOverlayOpen();
          if (overlayOpen === this.overlayOpen) return;
          this.overlayOpen = overlayOpen;
          this.update();
        });
        this.overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        if (this.newsletterPopup) {
          this.overlayObserver.observe(this.newsletterPopup, { attributes: true, attributeFilter: ['class'] });
        }

        this.buttonObserver = new MutationObserver(this.syncState);
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });

        if (this.mainPrice && this.priceContainer) {
          this.priceObserver = new MutationObserver(() => this.syncPrice());
          this.priceObserver.observe(this.mainPrice, { childList: true, subtree: true });
        }

        if (this.errorWrapper) {
          this.errorObserver = new MutationObserver(() => this.syncError());
          this.errorObserver.observe(this.errorWrapper, {
            attributes: true,
            attributeFilter: ['hidden'],
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        this.watchNotifyButton();
        this.setupResizeSelect();

        // Reserve space at the foot of the page so the bar never covers the footer. A real
        // element rather than body padding: overlays that set `body { height: 100% }` (the
        // cart drawer's .cart-overflow) would trap padding, shorten the page and clamp the
        // scroll position, leaving it moved after the overlay closes.
        this.resizeObserver = new ResizeObserver(() => {
          document.documentElement.style.setProperty('--cf-sticky-atc-height', `${this.bar.offsetHeight}px`);
        });
        this.resizeObserver.observe(this.bar);
        this.spacer = document.createElement('div');
        this.spacer.className = 'cf-sticky-atc-spacer';
        this.spacer.setAttribute('aria-hidden', 'true');
        document.body.append(this.spacer);
        document.documentElement.classList.add('cf-sticky-atc-enabled');

        this.syncState();
        this.hidden = false;
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        this.variantPicker?.removeEventListener('change', this.onVariantChange);
        this.optionSelects?.forEach((select) => select.removeEventListener('change', this.onOptionChange));
        this.braceletSelect?.removeEventListener('change', this.syncState);
        document.removeEventListener('focusin', this.onFocusIn);
        clearTimeout(this.focusCheckTimer);
        window.removeEventListener('scrollend', this.focusScrollEnd);
        if (!this.bar) return;
        this.headerSection?.removeEventListener('transitionend', this.scheduleHeaderCheck);
        cancelAnimationFrame(this.headerCheckFrame);
        [
          this.intersectionObserver,
          this.headerObserver,
          this.headerResizeObserver,
          this.overlayObserver,
          this.buttonObserver,
          this.priceObserver,
          this.errorObserver,
          this.resizeObserver,
          this.formObserver,
          this.notifyObserver,
          this.resizeSizeObserver,
          this.resizeNoteObserver,
        ].forEach((observer) => observer?.disconnect());
        this.resizeSelect?.closest('.cf-sticky-atc__option')?.remove();
        if (this.createdOptions) {
          this.createdOptions.remove();
          this.createdOptions = null;
          this.bar.classList.remove('cf-sticky-atc--has-options');
        }
        this.spacer?.remove();
        document.documentElement.classList.remove('cf-sticky-atc-enabled', 'cf-sticky-atc-visible');
        document.documentElement.style.removeProperty('--cf-sticky-atc-height');
      }

      /**
       * (Re)creates the observer on the button the bar mirrors (see getAnchor). The bar counts
       * as needed whenever that button is not fully visible above the fold: scrolled above the
       * viewport, or partly/fully under the sticky header. The huge bottom margin makes
       * "anywhere below the viewport" count as fully visible, so a jump straight from below to
       * above (Home/End, anchors, scroll restoration) still crosses a threshold and fires a callback.
       */
      observeMainButton() {
        const inset = this.getHeaderInset();
        const anchor = this.getAnchor();
        if (this.intersectionObserver && inset === this.headerInset && anchor === this.anchor) return;
        this.headerInset = inset;
        this.anchor = anchor;

        this.intersectionObserver?.disconnect();
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.scrolledPast = entry.intersectionRatio < 0.99;
            this.update();
          },
          { rootMargin: `-${inset}px 0px 100000px 0px`, threshold: [0, 0.99] }
        );
        this.intersectionObserver.observe(anchor);
      }

      /** The page button the bar stands in for: the Back in Stock button when sold out. */
      getAnchor() {
        return this.isNotifyMode() ? this.notifyButton : this.mainButton;
      }

      isNotifyMode() {
        return Boolean(
          this.mainButton.disabled && this.notifyButton && this.notifyButton.getClientRects().length > 0
        );
      }

      /** The Back in Stock app adds #BIS_trigger to the form after load; pick it up when it lands. */
      watchNotifyButton() {
        const button = this.form.querySelector('#BIS_trigger');
        if (button) {
          this.setNotifyButton(button);
          return;
        }
        this.formObserver = new MutationObserver(() => {
          const injected = this.form.querySelector('#BIS_trigger');
          if (!injected) return;
          this.formObserver.disconnect();
          this.setNotifyButton(injected);
        });
        this.formObserver.observe(this.form, { childList: true, subtree: true });
      }

      setNotifyButton(button) {
        this.notifyButton = button;
        // The theme hides it on watch products (inline style) and rewrites its content.
        this.notifyObserver = new MutationObserver(this.syncState);
        this.notifyObserver.observe(button, {
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden', 'aria-label'],
        });
        // The app's sign-up modal: a fixed iframe shown with an inline display style. It
        // doesn't lock page scroll, so watch it like the other overlays.
        this.notifyFrame = document.getElementById('BIS_frame');
        if (this.notifyFrame) {
          this.overlayObserver.observe(this.notifyFrame, { attributes: true, attributeFilter: ['style', 'class'] });
        }
        this.syncState();
      }

      /**
       * Ring resize (snippets/ring-resize.liquid + assets/ring-resize.js): the page lists the
       * sizes this ring can be resized to as buttons, already filtered by its resize rule. The
       * bar builds a select from those buttons and clicks them, so ring-resize.js keeps sole
       * ownership of the resize state, delivery message and fee that go into the cart.
       */
      setupResizeSelect() {
        const wrapper = document.getElementById('ring-resize-wrapper');
        const buttons = wrapper ? Array.from(wrapper.querySelectorAll('.size-button[data-size]')) : [];
        if (!buttons.length) return;
        this.resizeButtons = buttons;

        const id = `StickyAtcResize-${this.dataset.section}`;
        const option = document.createElement('div');
        option.className = 'cf-sticky-atc__option';
        const label = document.createElement('label');
        label.className = 'visually-hidden';
        label.htmlFor = id;
        label.textContent = this.dataset.labelRingSize;
        const selectWrapper = document.createElement('div');
        selectWrapper.className = 'select';
        const select = document.createElement('select');
        select.id = id;
        select.className = 'select__select cf-sticky-atc__select cf-sticky-atc__select--resize';
        buttons.forEach((button) => {
          const size = button.dataset.size;
          const current = button.dataset.isCurrent === 'true';
          select.add(new Option(current ? `${size} (${this.dataset.labelCurrentSize})` : size, size));
        });
        selectWrapper.append(select);
        // Same caret as the option selects (Dawn's icon-caret).
        selectWrapper.insertAdjacentHTML(
          'beforeend',
          '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>'
        );
        option.append(label, selectWrapper);

        let options = this.querySelector('.cf-sticky-atc__options');
        if (!options) {
          // Single-variant ring: the bar has no option selects yet, so add their container.
          options = document.createElement('div');
          options.className = 'cf-sticky-atc__options';
          this.button.before(options);
          this.bar.classList.add('cf-sticky-atc--has-options');
          this.createdOptions = options;
        }
        options.append(option);
        this.resizeSelect = select;

        select.addEventListener('change', () => {
          const button = this.resizeButtons.find((item) => item.dataset.size === select.value);
          if (button && !button.classList.contains('selected')) button.click();
          // ring-resize.js selects the size synchronously on click. If it didn't (script missing
          // or failed), snap back so the bar never shows a size that won't be ordered.
          this.syncResizeSelect();
        });

        // A size chosen on the page (or the bar's own click) moves the `selected` class.
        this.syncResizeSelect();
        this.resizeSizeObserver = new MutationObserver(() => this.syncResizeSelect());
        buttons.forEach((button) =>
          this.resizeSizeObserver.observe(button, { attributes: true, attributeFilter: ['class'] })
        );

        // Mirror the page's fee message ("Ring Resize Charges will be : £x" / "...is free").
        this.resizeNote = this.querySelector('.cf-sticky-atc__note');
        this.resizeStatus = this.querySelector('.cf-sticky-atc__note-status');
        this.resizePrice = wrapper.querySelector('#resize-price-display');
        if (this.resizeNote && this.resizePrice) {
          this.syncResizeNote();
          this.resizeNoteObserver = new MutationObserver(() => this.syncResizeNote());
          this.resizeNoteObserver.observe(this.resizePrice, {
            attributes: true,
            attributeFilter: ['style'],
            childList: true,
            characterData: true,
            subtree: true,
          });
        }
      }

      syncResizeSelect() {
        const selected = this.resizeButtons.find((button) => button.classList.contains('selected'));
        if (selected && this.resizeSelect.value !== selected.dataset.size) this.resizeSelect.value = selected.dataset.size;
      }

      syncResizeNote() {
        const text = this.resizePrice.style.display === 'none' ? '' : this.resizePrice.textContent.trim();
        this.resizeNote.textContent = text;
        this.resizeNote.hidden = !text;
        // The fee is a charge, so announce it. A live region that is itself toggled with `hidden`
        // is often not announced, so the always-present status element carries the text.
        if (this.resizeStatus && this.resizeStatus.textContent !== text) this.resizeStatus.textContent = text;
      }

      /** Height of the viewport's top edge covered by the sticky header, in px. */
      getHeaderInset() {
        if (!this.headerSection) return 0;
        const { position } = getComputedStyle(this.headerSection);
        if (position !== 'sticky' && position !== 'fixed') return 0;
        return Math.max(0, Math.round(this.headerSection.getBoundingClientRect().bottom));
      }

      scheduleHeaderCheck() {
        // The header's classes are rewritten on every scroll event; measure once per frame.
        cancelAnimationFrame(this.headerCheckFrame);
        this.headerCheckFrame = requestAnimationFrame(() => this.observeMainButton());
      }

      isOverlayOpen() {
        if (this.newsletterPopup?.classList.contains('is-visible')) return true;
        if (this.notifyFrame && getComputedStyle(this.notifyFrame).display !== 'none') return true;
        return Array.from(document.body.classList).some(
          (name) => name === 'overflow-hidden' || name.startsWith('overflow-hidden-')
        );
      }

      update() {
        // A hidden anchor (sold out with no Back in Stock button, e.g. watches) never "intersects",
        // so it would always read as scrolled past: there is nothing to stand in for, keep hidden.
        const show = this.scrolledPast && !this.overlayOpen && this.anchor.getClientRects().length > 0;
        if (!this.overlayOpen && !show) this.restoreFocusOnClose = false;
        if (show === this.visible) return;
        this.visible = show;

        const focusInBar = this.bar.contains(document.activeElement);
        if (!show && this.overlayOpen) {
          // The overlay was opened from the bar (e.g. cart drawer after add). The drawer
          // tries to return focus here on close, while the bar is still inert.
          this.restoreFocusOnClose = focusInBar;
        } else if (!show && focusInBar) {
          // Scrolled back to the main button: hand focus to it rather than losing it to <body>.
          this.anchor.focus({ preventScroll: true });
        }

        this.bar.inert = !show;
        this.bar.classList.toggle('is-visible', show);
        // Drives scroll-padding so focused elements are never hidden behind the bar.
        document.documentElement.classList.toggle('cf-sticky-atc-visible', show);

        if (show && this.restoreFocusOnClose) {
          // Dialog pattern: focus returns to the control that opened it. The drawer's own
          // attempt failed because the bar was still inert, leaving focus in the closing drawer.
          this.restoreFocusOnClose = false;
          this.button.focus({ preventScroll: true });
        }
      }

      /**
       * WCAG 2.2 2.4.11 fallback: WebKit ignores scroll-padding when scrolling a newly
       * focused element into view, so nudge anything that lands behind the bar.
       */
      onFocusIn(event) {
        const target = event.target;
        if (!this.canNudge(target)) return;

        let nudged = false;
        const check = () => {
          // Re-test: the bar may have hidden, or an overlay opened, since focus landed.
          if (nudged || document.activeElement !== target || !this.canNudge(target)) return;
          const barTop = this.bar.getBoundingClientRect().top;
          const rect = target.getBoundingClientRect();
          // Skip anything too tall to fit above the bar: scrolling can't clear it.
          if (rect.height && rect.height < barTop && rect.bottom > barTop && rect.top < window.innerHeight) {
            // At most one nudge per focus, so it can never chain into a runaway scroll.
            nudged = true;
            window.scrollBy({ top: rect.bottom - barTop + 16 });
          }
        };
        // Check now, and again once the browser's own (possibly smooth) focus scroll has
        // settled: on scrollend where supported, with a timer fallback for older engines.
        requestAnimationFrame(check);
        window.removeEventListener('scrollend', this.focusScrollEnd);
        this.focusScrollEnd = check;
        window.addEventListener('scrollend', check, { once: true });
        clearTimeout(this.focusCheckTimer);
        this.focusCheckTimer = setTimeout(check, 1000);
      }

      /**
       * Only in-flow page content can be scrolled clear of the bar. Elements in drawers,
       * dialogs or the sticky header are fixed/sticky: scrolling the page never moves them.
       */
      canNudge(target) {
        if (!this.visible || this.overlayOpen || this.isOverlayOpen()) return false;
        if (!(target instanceof Element) || this.bar.contains(target)) return false;
        // WebKit doesn't focus a clicked button; focus falls to the nearest focusable
        // ancestor, e.g. <main tabindex="-1">, which contains the bar itself.
        if (target.contains(this.bar)) return false;
        for (let node = target; node && node !== document.body; node = node.parentElement) {
          const { position } = getComputedStyle(node);
          if (position === 'fixed' || position === 'sticky') return false;
        }
        return true;
      }

      onClick() {
        if (this.isNotifyMode()) {
          this.notifyButton.click();
          return;
        }
        if (this.button.getAttribute('aria-disabled') === 'true') return;

        const field = this.getUnresolvedField();
        if (field) {
          this.focusField(field);
          return;
        }

        this.mainButton.click();
      }

      onVariantChange() {
        // Read the picker's own inputs rather than VariantSelects.currentVariant: whether its
        // change handler has run before this one depends on script order, so it can be stale.
        this.variantChosen = true;
        const options = this.getPageOptions();
        this.syncImage(this.findVariant(options));
        // Mirror a choice made on the page (or one this bar pushed) into the bar's selects.
        this.optionSelects.forEach((select) => {
          const value = options[Number(select.dataset.optionIndex)];
          if (value != null && select.value !== value) select.value = value;
        });
        this.syncState();
      }

      /** The variant matching these option values, from the picker's embedded variant JSON. */
      findVariant(options) {
        if (!this.variants) {
          const data = this.variantPicker?.querySelector('script[type="application/json"]');
          try {
            this.variants = data ? JSON.parse(data.textContent) : [];
          } catch (error) {
            this.variants = [];
          }
        }
        return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
      }

      /** A choice made in the bar: once every option has a value, apply it to the page's picker. */
      onOptionChange() {
        if (this.optionSelects.some((select) => !select.value)) {
          this.syncState();
          return;
        }
        const current = this.getPageOptions();
        this.optionSelects.forEach((select) => {
          const index = Number(select.dataset.optionIndex);
          if (current[index] !== select.value) this.setPageOption(index, select.dataset.optionName, select.value);
        });
        // Picking the value the page already had fires no change on the page, but is still a choice.
        this.variantChosen = true;
        this.syncState();
      }

      /** Current option values of the page's picker (Dawn's selects or radios). */
      getPageOptions() {
        if (!this.variantPicker) return [];
        const selects = this.variantPicker.querySelectorAll('select');
        if (selects.length) return Array.from(selects, (select) => select.value);
        return Array.from(this.variantPicker.querySelectorAll('fieldset'), (fieldset) => {
          const checked = fieldset.querySelector('input:checked');
          return checked ? checked.value : null;
        });
      }

      /**
       * Select an option value on the page. Swatch King renders the visible picker and
       * writes to Dawn's hidden selects itself; changing those directly would leave its
       * UI showing the old value, so click its own swatch instead when it is there.
       */
      setPageOption(index, name, value) {
        const swatch = this.productSection.querySelector(
          `variant-swatch-king [option-name="${CSS.escape(name)}"] [orig-value="${CSS.escape(value)}"]`
        );
        if (swatch) {
          swatch.click();
          return;
        }
        if (!this.variantPicker) return;

        const select = this.variantPicker.querySelectorAll('select')[index];
        if (select) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
        const fieldset = this.variantPicker.querySelectorAll('fieldset')[index];
        const radio = fieldset && fieldset.querySelector(`input[value="${CSS.escape(value)}"]`);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      /**
       * The first form field that still needs the shopper's input, or null.
       * A multi-variant product counts as chosen once the URL names a variant,
       * the picker has changed, or the shopper has been taken to the options.
       * With option selects in the bar, the choice is made right there instead.
       */
      getUnresolvedField() {
        if (this.optionSelects.length) {
          const empty = this.optionSelects.find((select) => !select.value);
          if (empty) return empty;
        } else if (!this.variantChosen && this.variantPicker) {
          const firstOption = this.variantPicker.querySelector('fieldset, .product-form__input');
          const field = firstOption && firstOption.querySelector('input:checked, select, input');
          if (field) return field;
        }

        if (this.braceletSelect && this.braceletSelect.value === '_') return this.braceletSelect;

        return null;
      }

      focusField(field) {
        if (this.bar.contains(field)) {
          // The bar's own select: no need to leave the bar. Open it where supported
          // (this runs from the button's click, so the user activation is there).
          field.focus({ preventScroll: true });
          try {
            field.showPicker?.();
          } catch (error) {
            // Not supported for <select> in this browser; focus is enough.
          }
          return;
        }
        if (this.variantPicker && this.variantPicker.contains(field)) this.variantChosen = true;

        const target = field.closest('fieldset, .product-form__input, .size-selector-wrapper') || field;
        target.scrollIntoView({
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
          block: 'center',
        });
        field.focus({ preventScroll: true });
        this.syncState();
      }

      syncState() {
        const notify = this.isNotifyMode();
        const busy =
          !notify &&
          (this.mainButton.getAttribute('aria-disabled') === 'true' ||
            this.mainButton.classList.contains('loading') ||
            this.mainButton.classList.contains('is-loading'));
        const needsChoice = !notify && !busy && this.getUnresolvedField() !== null;
        const unavailable = !notify && !busy && !needsChoice && this.mainButton.disabled;

        let label = this.dataset.labelAdd;
        if (notify) label = this.dataset.labelNotify;
        else if (needsChoice) label = this.dataset.labelSelect;
        else if (unavailable) label = this.dataset.labelSoldOut;
        this.buttonLabel.textContent = label;

        // Same accessible name as the app's own button ("NOTIFY ME WHEN AVAILABLE").
        const notifyName = notify && this.notifyButton.getAttribute('aria-label');
        if (notifyName) this.button.setAttribute('aria-label', notifyName);
        else this.button.removeAttribute('aria-label');
        // Both the cart drawer and the Back in Stock sign-up open as dialogs.
        if (notify || this.hasCartDrawer) this.button.setAttribute('aria-haspopup', 'dialog');
        else this.button.removeAttribute('aria-haspopup');

        this.icon.classList.toggle('hidden', notify || needsChoice || unavailable);
        this.notifyIcon?.classList.toggle('hidden', !notify);
        this.spinner.classList.toggle('hidden', !busy);
        this.button.classList.toggle('loading', busy);
        this.button.classList.toggle('cf-sticky-atc__button--unavailable', unavailable);
        this.button.classList.toggle('cf-sticky-atc__button--notify', notify);
        this.button.disabled = unavailable;
        // aria-disabled (not disabled) while busy so keyboard focus stays on the button.
        if (busy) this.button.setAttribute('aria-disabled', 'true');
        else this.button.removeAttribute('aria-disabled');

        // Switching between the add and notify buttons changes what the bar tracks.
        this.observeMainButton();
      }

      syncPrice() {
        const source = this.mainPrice.querySelector('.price__container');
        if (source) this.priceContainer.innerHTML = source.innerHTML;
      }

      syncImage(variant) {
        const src = variant && variant.featured_media && variant.featured_media.preview_image?.src;
        if (!src || !this.image) return;

        const sized = (width) => {
          const url = new URL(src, window.location.origin);
          url.searchParams.set('width', width);
          return url.href;
        };
        this.image.srcset = `${sized(120)} 120w, ${sized(240)} 240w`;
        this.image.src = sized(240);
      }

      syncError() {
        const message = this.errorWrapper.querySelector('.product-form__error-message');
        const text = !this.errorWrapper.hidden && message ? message.textContent.trim() : '';
        this.error.textContent = text;
        this.error.hidden = !text;
      }
    }
  );
}
